import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Room, RoomEvent } from 'livekit-client';
import { HydratedPost, VoiceParticipant } from '../types';
import { PostCard } from './PostCard';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { API_URL } from '../api';

interface FeaturedPostsProps {
    posts: HydratedPost[];
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onNavigateToProfile: (userId: string) => void;
    onHoldProfile?: (userId: string) => void;
    onLeaveProfile?: () => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    onVouch?: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    isModalOpen?: boolean;
    onAddToCart?: (post: any) => void;
    isShakingMode?: boolean;
    postcardGap?: number;
    voiceRooms?: Record<string, VoiceParticipant[]>;
    voiceRemoteStreams?: Record<string, MediaStream>;
    voiceLocalStream?: MediaStream | null;
    communities?: any[];
    isSticky?: boolean;
    onToggleSticky?: () => void;
    onPostDragProgress?: (post: any, progress: number, direction: 'left' | 'right' | null) => void;
}

interface ActiveLiveStream {
    username: string;
    communityId: string;
    channelId: string;
    communityName: string;
    communityAvatar?: string;
    channelName?: string;
    stream?: MediaStream | null;
}

// Fisher-Yates shuffle helper
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export const FeaturedPosts: React.FC<FeaturedPostsProps> = ({
    posts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onHoldProfile,
    onLeaveProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onShare,
    onVouch,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    onReportPost,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    isModalOpen,
    onAddToCart,
    isShakingMode,
    postcardGap,
    voiceRooms = {},
    voiceRemoteStreams = {},
    voiceLocalStream = null,
    communities = [],
    isSticky = true,
    onToggleSticky,
    onPostDragProgress
}) => {
    const { currentUser, usersData } = useAuth() || {};
    const currentUserData = currentUser ? usersData?.[currentUser] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';

    const [isFeaturedVisible, setIsFeaturedVisible] = useState(true);

    // Track viewport width for desktop row sizing (4 posts wide on desktop vs 1 on mobile)
    const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const itemsPerRow = isDesktop ? 4 : 1;

    const [livePreviewStreams, setLivePreviewStreams] = useState<Record<string, MediaStream>>({});

    // Background LiveKit subscriber: streams public community screen shares to home feed before user joins voice channel
    useEffect(() => {
        if (!currentUser || !voiceRooms || communities.length === 0) return;

        const activePublicLiveRooms: Array<{ communityId: string; channelId: string; username: string }> = [];
        Object.entries(voiceRooms).forEach(([roomKey, participants]) => {
            if (!Array.isArray(participants) || participants.length === 0) return;
            const parts = roomKey.split(':');
            if (parts.length < 2) return;
            const communityId = parts[0];
            const channelId = parts[1];
            const comm = communities.find((c: any) => String(c._id) === String(communityId) || String(c.id) === String(communityId));
            if (!comm || comm.isPrivate || comm.visibility === 'private') return;

            participants.forEach(p => {
                if (p.isScreenSharing && p.username !== currentUser) {
                    activePublicLiveRooms.push({ communityId, channelId, username: p.username });
                }
            });
        });

        if (activePublicLiveRooms.length === 0) return;

        const activeRooms: Room[] = [];

        activePublicLiveRooms.forEach(async (live) => {
            const roomName = `${live.communityId}:${live.channelId}`;
            const previewKey = `${live.communityId}:${live.channelId}:${live.username.toLowerCase()}`;

            const screenKey = `${live.username.toLowerCase()}-screen`;
            if (voiceRemoteStreams?.[screenKey]) return; // Already subscribed in voice room

            try {
                const previewIdentity = `${currentUser}_feed_preview_${Math.floor(Math.random() * 10000)}`;
                const res = await fetch(`${API_URL}livekit/token?room=${encodeURIComponent(roomName)}&identity=${encodeURIComponent(previewIdentity)}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!data.token) return;

                const room = new Room({
                    adaptiveStream: true,
                    dynacast: true
                });
                activeRooms.push(room);

                const updatePreviewStream = () => {
                    let foundTrack: any = null;
                    room.remoteParticipants.forEach(participant => {
                        if (participant.identity.toLowerCase() === live.username.toLowerCase()) {
                            Array.from(participant.trackPublications.values()).forEach(pub => {
                                if (pub.source === 'screen_share' && (pub.track || pub.videoTrack)) {
                                    foundTrack = pub.track || pub.videoTrack;
                                }
                            });
                        }
                    });

                    if (foundTrack && foundTrack.mediaStreamTrack) {
                        const mediaStream = new MediaStream([foundTrack.mediaStreamTrack]);
                        setLivePreviewStreams(prev => ({ ...prev, [previewKey]: mediaStream }));
                    }
                };

                room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                    if (participant.identity.toLowerCase() === live.username.toLowerCase()) {
                        updatePreviewStream();
                    }
                });

                room.on(RoomEvent.ParticipantConnected, () => updatePreviewStream());

                const wsTarget = data.wsUrl || data.url || 'wss://livekit.goodlyfe.net';
                await room.connect(wsTarget, data.token);
                updatePreviewStream();
            } catch (e) {
                console.warn('[FeaturedPosts] Background LiveKit subscriber error:', e);
            }
        });

        return () => {
            activeRooms.forEach(r => {
                try { r.disconnect(); } catch {}
            });
        };
    }, [voiceRooms, communities, currentUser, voiceRemoteStreams]);

    // Active live screen streams in public communities
    const activeLiveStreams = useMemo<ActiveLiveStream[]>(() => {
        if (!voiceRooms || !communities || communities.length === 0) return [];
        const list: ActiveLiveStream[] = [];

        Object.entries(voiceRooms).forEach(([roomKey, participants]) => {
            if (!Array.isArray(participants) || participants.length === 0) return;

            const parts = roomKey.split(':');
            if (parts.length < 2) return;
            const communityId = parts[0];
            const channelId = parts[1];

            // Match public community
            const comm = communities.find((c: any) => String(c._id) === String(communityId) || String(c.id) === String(communityId));
            if (!comm) return;
            if (comm.isPrivate || comm.visibility === 'private') return;

            participants.forEach(p => {
                if (p.isScreenSharing) {
                    const chan = (comm.channels || []).find((ch: any) => String(ch.id) === String(channelId) || String(ch._id) === String(channelId));
                    const isLocal = p.username === currentUser;
                    const screenKey = `${p.username.toLowerCase()}-screen`;
                    const previewKey = `${communityId}:${channelId}:${p.username.toLowerCase()}`;
                    const streamObj = isLocal 
                        ? voiceLocalStream 
                        : (voiceRemoteStreams?.[screenKey] || livePreviewStreams[previewKey] || voiceRemoteStreams?.[p.socketId] || voiceRemoteStreams?.[p.username] || voiceRemoteStreams?.[p.username.toLowerCase()] || (p as any).stream || null);
                    list.push({
                        username: p.username,
                        communityId,
                        channelId,
                        communityName: comm.name,
                        communityAvatar: comm.avatar,
                        channelName: chan?.name || 'voice',
                        stream: streamObj
                    });
                }
            });
        });

        return list;
    }, [voiceRooms, communities, currentUser, voiceLocalStream, voiceRemoteStreams, livePreviewStreams]);

    const storageKey = `featured_posts_filter_${currentUser || 'default'}`;
    const [filterMode, setFilterMode] = useState<'all' | 'connections' | 'live'>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved === 'all' || saved === 'connections' || saved === 'live') return saved as any;
        } catch {}
        return 'all';
    });

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved === 'all' || saved === 'connections' || saved === 'live') {
                setFilterMode(saved as any);
            }
        } catch {}
    }, [storageKey]);

    // Fallback if live filter is active but no active lives remain
    useEffect(() => {
        if (filterMode === 'live' && activeLiveStreams.length === 0) {
            setFilterMode('all');
        }
    }, [filterMode, activeLiveStreams.length]);

    const handleFilterChange = (mode: 'all' | 'connections' | 'live') => {
        setFilterMode(mode);
        try {
            localStorage.setItem(storageKey, mode);
        } catch {}
    };

    const [currentIndex, setCurrentIndex] = useState(0);

    // Build connections set: friends, followers, following + currentUser
    const connectionsSet = useMemo(() => {
        if (!currentUserData && !currentUser) return new Set<string>();
        const friends = currentUserData?.friends || [];
        const followers = currentUserData?.followers || [];
        const following = currentUserData?.following || [];
        const self = currentUser ? [currentUser] : [];
        return new Set<string>([...friends, ...followers, ...following, ...self]);
    }, [currentUserData, currentUser]);

    // Filter base public posts
    const eligiblePublicPosts = useMemo(() => {
        return posts.filter(p => {
            if (p.isArchived) return false;
            if (p.communityId) return false; // Exclude community-only posts
            if (p.visibility !== 'public') return false; // Strictly public posts

            if (filterMode === 'connections') {
                return connectionsSet.has(p.author);
            }
            return true;
        });
    }, [posts, filterMode, connectionsSet]);

    // Maintain a shuffled list of eligible posts
    const [shuffledPosts, setShuffledPosts] = useState<HydratedPost[]>([]);

    useEffect(() => {
        if (filterMode === 'live') {
            setCurrentIndex(0);
        } else if (eligiblePublicPosts.length > 0) {
            setShuffledPosts(shuffleArray(eligiblePublicPosts));
            setCurrentIndex(0);
        } else {
            setShuffledPosts([]);
            setCurrentIndex(0);
        }
    }, [eligiblePublicPosts, filterMode]);

    const activeListLength = filterMode === 'live' ? activeLiveStreams.length : shuffledPosts.length;

    // Derived visible posts for the active row
    const visiblePosts = useMemo(() => {
        if (activeListLength === 0) return [];
        const count = Math.min(itemsPerRow, activeListLength);
        const result: Array<{ post: HydratedPost; key: string }> = [];

        for (let i = 0; i < count; i++) {
            const idx = (currentIndex + i) % activeListLength;
            if (filterMode === 'live') {
                const live = activeLiveStreams[idx];
                if (live) {
                    result.push({
                        post: {
                            id: -999999 - idx,
                            author: live.username,
                            createdAt: new Date().toISOString(),
                            type: 'live_stream' as any,
                            visibility: 'public',
                            communityId: live.communityId,
                            isLive: true,
                            liveStream: live,
                            content: {
                                text: `🔴 LIVE SCREEN SHARE in #${live.channelName || 'voice'}`,
                                media: []
                            },
                            likes: [],
                            bookmarks: [],
                            bumps: [],
                            vouches: [],
                            comments: [],
                            isLiked: false,
                            isBookmarked: false,
                            isBumped: false
                        },
                        key: `live-${live.communityId}-${live.channelId}-${live.username}`
                    });
                }
            } else {
                const p = shuffledPosts[idx];
                if (p) {
                    result.push({
                        post: p,
                        key: `post-${p.id}`
                    });
                }
            }
        }
        return result;
    }, [currentIndex, itemsPerRow, activeListLength, filterMode, activeLiveStreams, shuffledPosts]);

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [isAutoPaused, setIsAutoPaused] = useState(false);

    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        if (activeListLength > 1 && !isAutoPaused && !isModalOpen) {
            timerRef.current = setInterval(() => {
                setCurrentIndex(prev => (prev + 1) % activeListLength);
            }, 8000);
        }
    }, [activeListLength, isAutoPaused, isModalOpen]);

    useEffect(() => {
        resetTimer();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [resetTimer]);

    const handleNext = () => {
        if (activeListLength === 0) return;
        setCurrentIndex(prev => (prev + 1) % activeListLength);
        resetTimer();
    };

    const handlePrev = () => {
        if (activeListLength === 0) return;
        setCurrentIndex(prev => (prev - 1 + activeListLength) % activeListLength);
        resetTimer();
    };

    const handlePostSelect = (selectedPost: HydratedPost) => {
        setIsAutoPaused(true);
        onPostClick?.(selectedPost);
    };

    return (
        <div className="w-full my-[2vh] flex flex-col items-center px-[6vw]">
            <div className="w-full max-w-[1400px] flex items-center justify-between mb-[1.5vh]">
                <div className="flex items-center gap-3">
                    <button 
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSticky?.(); }}
                        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSticky?.(); }}
                        className="text-[0.95rem] font-bold tracking-wide flex items-center gap-[1.5vw] cursor-pointer hover:opacity-80 active:scale-95 transition-all select-none bg-transparent border-none p-0 text-left"
                        style={{ color: userTextColor, ...stickerTextStyle(0.18) }}
                        title={isSticky ? "Pinned to top (Click to unpin)" : "Unpinned (Click to pin to top)"}
                    >
                        <span className="text-[1.1rem]">{isSticky ? '❌ ' : '📌 '} </span>  ✨ Featured Lyfe ✨
                    </button>

                    {/* Auto-Next Pause/Play Icon Button */}
                    <button
                        onClick={() => setIsAutoPaused(prev => !prev)}
                        className={`p-1.5 rounded-full transition-all duration-200 border flex items-center justify-center cursor-pointer active:scale-95 ${
                            isAutoPaused 
                                ? 'bg-amber-500/20 border-amber-500/40 text-amber-300 hover:bg-amber-500/30' 
                                : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20 hover:text-white'
                        }`}
                        title={isAutoPaused ? "Auto-Next Paused (Click to resume)" : "Auto-Next Playing (Click to pause)"}
                        aria-label={isAutoPaused ? "Resume Auto-Next" : "Pause Auto-Next"}
                    >
                        {isAutoPaused ? (
                            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        ) : (
                            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                            </svg>
                        )}
                    </button>
                </div>

                <div className="flex items-center bg-black/30 p-[0.3vh] rounded-full border border-white/10 text-[0.8rem]">
                    {activeLiveStreams.length > 0 && (
                        <button
                            onClick={() => handleFilterChange('live')}
                            className={`px-[1.2vw] py-[0.5vh] rounded-full transition-all duration-200 font-bold flex items-center gap-1.5 ${
                                filterMode === 'live' 
                                    ? 'shadow-md text-white' 
                                    : 'text-grey-400 hover:text-white'
                            }`}
                            style={filterMode === 'live' ? { backgroundColor: userThemeColor } : {}}
                        >
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            LIVE
                        </button>
                    )}
                    <button
                        onClick={() => handleFilterChange('all')}
                        className={`px-[1.2vw] py-[0.5vh] rounded-full transition-all duration-200 font-semibold ${
                            filterMode === 'all' 
                                ? 'shadow-md text-white' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                        style={filterMode === 'all' ? { backgroundColor: userThemeColor } : {}}
                    >
                        Public
                    </button>
                    <button
                        onClick={() => handleFilterChange('connections')}
                        className={`px-[1.2vw] py-[0.5vh] rounded-full transition-all duration-200 font-semibold ${
                            filterMode === 'connections' 
                                ? 'shadow-md text-white' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                        style={filterMode === 'connections' ? { backgroundColor: userThemeColor } : {}}
                    >
                        Connections
                    </button>
                </div>
            </div>

            {visiblePosts.length > 0 ? (
                <div className="w-full max-w-[1400px] flex items-stretch gap-2">
                    <button
                        onClick={handlePrev}
                        disabled={activeListLength <= 1}
                        className="flex items-center justify-center w-8 sm:w-10 flex-shrink-0 rounded-2xl hover:bg-white/5 active:bg-white/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed text-white/40 hover:text-white/80 border border-white/10"
                        title="Previous Post"
                        aria-label="Previous Featured Post"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Post Cards Shifting Row Grid */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <div className={`grid gap-4 w-full ${isDesktop ? 'grid-cols-4' : 'grid-cols-1'}`}>
                            <AnimatePresence mode="popLayout" initial={false}>
                                {visiblePosts.map(({ post, key }) => (
                                    <motion.div
                                        key={key}
                                        layout
                                        initial={{ opacity: 0, x: 50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -50 }}
                                        transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
                                        className="w-full min-w-0"
                                        onClickCapture={() => {
                                            setIsAutoPaused(true);
                                        }}
                                    >
                                        <PostCard
                                            post={post}
                                            onPostClick={handlePostSelect}
                                            onLike={onLike}
                                            onBump={onBump}
                                            onBookmark={onBookmark}
                                            onEdit={onEdit}
                                            onDelete={onDelete}
                                            onNavigateToProfile={onNavigateToProfile}
                                            onHoldProfile={onHoldProfile}
                                            onLeaveProfile={onLeaveProfile}
                                            onNavigateToHashtag={onNavigateToHashtag}
                                            onNavigateToCommunity={onNavigateToCommunity}
                                            onShare={onShare}
                                            onVouch={onVouch}
                                            onMuteUser={onMuteUser}
                                            onBlockUser={onBlockUser}
                                            onGrantAdmin={onGrantAdmin}
                                            onDeleteUser={onDeleteUser}
                                            onReportPost={onReportPost}
                                            animatingLikes={animatingLikes}
                                            animatingBumps={animatingBumps}
                                            animatingBookmarks={animatingBookmarks}
                                            galleryAnimation={galleryAnimation}
                                            postBlurOpacity={postBlurOpacity}
                                            columns={4}
                                            viewerPostcardDefault={currentUserData?.postcardDefault}
                                            viewerPostcardAppearance={currentUserData?.postcardAppearance}
                                            isModalOpen={isModalOpen}
                                            onAddToCart={onAddToCart}
                                            isHome={true}
                                            isShakingMode={isShakingMode}
                                            postcardGap={postcardGap}
                                            onPostDragProgress={onPostDragProgress}
                                        />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Right Arrow Button */}
                    <button
                        onClick={handleNext}
                        disabled={activeListLength <= 1}
                        className="flex items-center justify-center w-8 sm:w-10 flex-shrink-0 rounded-2xl hover:bg-white/5 active:bg-white/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed text-white/40 hover:text-white/80 border border-white/10"
                        title="Next Post"
                        aria-label="Next Featured Post"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            ) : (
                <div 
                    className="w-full max-w-[1400px] p-[3vh] rounded-[2rem] bg-black/20 border border-white/10 text-center text-gray-400 text-[0.9rem]"
                    style={{ color: userTextColor }}
                >
                    No featured posts found for {filterMode === 'connections' ? 'connections' : filterMode === 'live' ? 'live streams' : 'public'}.
                </div>
            )}
        </div>
    );
};
